import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { constructionServices, engineeringServices } from '../data/services';
import SectionHeading from '../components/ui/SectionHeading';
import AnimatedSection from '../components/ui/AnimatedSection';
import Button from '../components/ui/Button';
import { getIcon } from '../lib/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import CTABanner from '../components/sections/CTABanner';

function renderParagraph(paragraph: string, idx: number) {
  if (paragraph.includes('- ')) {
    const lines = paragraph.split('\n');
    return (
      <ul key={idx} className="space-y-2 ml-4">
        {lines.map((line, lineIdx) => (
          <li key={lineIdx} className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
            <span>{line.replace('- ', '')}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p key={idx}>{paragraph}</p>;
}

function ServiceDetailCard({
  id,
  title,
  fullDescription,
  icon,
  features,
  index,
}: {
  id: string;
  title: string;
  fullDescription: string;
  icon: string;
  features?: string[];
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const IconComponent = getIcon(icon);
  const paragraphs = fullDescription.split('\n\n').filter(Boolean);
  const firstParagraph = paragraphs[0];
  const remainingParagraphs = paragraphs.slice(1);
  const hasMore = remainingParagraphs.length > 0;

  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="scroll-mt-24"
    >
      <div className="bg-white rounded-2xl border border-charcoal-100 overflow-hidden hover:shadow-lg transition-shadow">
        <div className="p-8 md:p-10">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-4 bg-primary-50 rounded-xl flex-shrink-0">
              <IconComponent className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-charcoal-900 mb-1">
                {title}
              </h3>
            </div>
          </div>

          <div className="space-y-4 text-charcoal-600 leading-relaxed">
            {renderParagraph(firstParagraph, 0)}

            {hasMore && (
              <>
                {/* Desktop: always visible */}
                <div className="hidden md:block space-y-4">
                  {remainingParagraphs.map((p, idx) => renderParagraph(p, idx + 1))}
                </div>

                {/* Mobile: animated expand/collapse */}
                <div className="md:hidden overflow-hidden">
                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.div
                        key="mobile-expanded"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="space-y-4"
                      >
                        {remainingParagraphs.map((p, idx) => renderParagraph(p, idx + 1))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={() => setExpanded(!expanded)}
                  className="md:hidden flex items-center gap-1 text-primary-600 font-semibold text-sm hover:text-primary-700 transition-colors"
                >
                  {expanded ? (
                    <>Show less <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Read more <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              </>
            )}
          </div>

          {features && features.length > 0 && (
            <div className="mt-8 pt-6 border-t border-charcoal-100">
              <h4 className="font-semibold text-charcoal-900 mb-4">
                Key Capabilities
              </h4>
              <div className="flex flex-wrap gap-2">
                {features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-primary-50 text-primary-700 text-sm font-medium rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <Button href="/contact">
              Discuss Your Project
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-b from-charcoal-900 to-charcoal-800">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="heading-1 text-white mb-6">
                Our Services
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-charcoal-200 mb-4">
                Complete Construction & Engineering Solutions
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section-padding bg-charcoal-50">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeading
              subtitle="Construction Services"
              title="Building Excellence"
              description="Our construction services cover every aspect of building development, from foundation to finishing."
              align="center"
            />
          </AnimatedSection>

          <div className="space-y-8 mt-12">
            {constructionServices.map((service, index) => (
              <ServiceDetailCard
                key={service.id}
                id={service.id}
                title={service.title}
                fullDescription={service.fullDescription}
                icon={service.icon}
                features={service.features}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeading
              subtitle="Engineering Services"
              title="Technical Excellence"
              description="Our engineering services provide the technical foundation for successful construction projects."
              align="center"
            />
          </AnimatedSection>

          <div className="space-y-8 mt-12">
            {engineeringServices.map((service, index) => (
              <ServiceDetailCard
                key={service.id}
                id={service.id}
                title={service.title}
                fullDescription={service.fullDescription}
                icon={service.icon}
                features={service.features}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
